import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
    TrendingUp, TrendingDown, Target, Wallet, Filter, Loader2, Download,
    Search, Check, ChevronDown, X, RefreshCw, Calendar, Building2, Users,
    Truck, ShoppingCart, Receipt, FileText, HardHat, Banknote,
    Printer, ArrowDownCircle, ArrowUpCircle, AlertCircle, ShieldCheck
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';
import * as XLSX from 'xlsx';
import api from '../api/axiosInstance';
import { AuthContext } from '../App';

// Palette for cost breakdowns
const COST_COLORS = {
    employee: '#8B5CF6',   // Purple / Labour
    vendor: '#4F46E5',     // Indigo / Vendor
    petty_cash: '#10B981', // Emerald / Petty Cash
    material: '#F59E0B',   // Amber / Direct Materials
};

export default function FinancialBreakdown() {
    const { user } = useContext(AuthContext);

    // Filter states
    const [duration, setDuration] = useState('1month'); // '1month' | '3months' | '6months' | 'overall'
    const [selectedProjects, setSelectedProjects] = useState([]); // Empty = All projects
    const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
    const [projectSearchQuery, setProjectSearchQuery] = useState('');
    const dropdownRef = useRef(null);

    // Active tab
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'income' | 'expenses' | 'employee' | 'vendor' | 'petty' | 'material'
    const [tableSearch, setTableSearch] = useState('');

    // Data states
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [projectsList, setProjectsList] = useState([]);
    const [financialData, setFinancialData] = useState({
        summary: {
            total_income: 0,
            total_expenses: 0,
            net_profit: 0,
            profit_margin: 0,
            breakdown: { employee: 0, vendor: 0, petty_cash: 0, material: 0 },
            counts: {
                income_transactions: 0,
                total_expense_transactions: 0,
                employee_records: 0,
                vendor_payments: 0,
                petty_records: 0,
                material_purchases: 0
            }
        },
        timeline: [],
        project_breakdown: [],
        income: [],
        all_expenses: [],
        employee_expenses: [],
        vendor_payments: [],
        petty_cash: [],
        material_spent: []
    });

    // Close project dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsProjectDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch initial projects list once
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const res = await api.get('get_projects.php');
                if (res.data && res.data.projects) {
                    setProjectsList(res.data.projects);
                }
            } catch (err) {
                console.error("Error fetching projects list:", err);
            }
        };
        fetchProjects();
    }, []);

    // Fetch main financial breakdown whenever duration or selected projects change
    useEffect(() => {
        fetchFinancialBreakdown();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duration, selectedProjects]);

    // Calculate human-readable date bounds for current duration
    const dateRangeLabel = useMemo(() => {
        const now = new Date();
        const formatDate = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        const endStr = formatDate(now);

        if (duration === '1month') {
            const start = new Date();
            start.setMonth(start.getMonth() - 1);
            return `${formatDate(start)} – ${endStr}`;
        }
        if (duration === '3months') {
            const start = new Date();
            start.setMonth(start.getMonth() - 3);
            return `${formatDate(start)} – ${endStr}`;
        }
        if (duration === '6months') {
            const start = new Date();
            start.setMonth(start.getMonth() - 6);
            return `${formatDate(start)} – ${endStr}`;
        }
        return 'All Time Inflows & Outflows';
    }, [duration]);

    // Helper: Compute date filter boundary for client-side fallback
    const getStartDateForDuration = (dur) => {
        const now = new Date();
        if (dur === '1month') {
            now.setMonth(now.getMonth() - 1);
            return now.toISOString().split('T')[0];
        }
        if (dur === '3months') {
            now.setMonth(now.getMonth() - 3);
            return now.toISOString().split('T')[0];
        }
        if (dur === '6months') {
            now.setMonth(now.getMonth() - 6);
            return now.toISOString().split('T')[0];
        }
        return '1970-01-01';
    };

    // Robust client-side fallback aggregator: computes Total Salary per Employee for selected range and project
    const fallbackAggregateData = async () => {
        const startDate = getStartDateForDuration(duration);
        const endDate = new Date().toISOString().split('T')[0];

        try {
            // 1. Fetch primary accounting records
            const [clientInvRes, vendorInvRes, matRes, pettyRes, projRes] = await Promise.all([
                api.get('get_client_invoices.php').catch(() => ({ data: { data: [] } })),
                api.get('get_all_vendor_invoices.php').catch(() => ({ data: { data: [] } })),
                api.get('get_material_purchases.php').catch(() => ({ data: { purchases: [] } })),
                api.get('get_admin_petty_cash.php').catch(() => api.get('get_petty_cash.php').catch(() => ({ data: { logs: [] } }))),
                api.get('get_projects.php').catch(() => ({ data: { projects: [] } }))
            ]);

            const clientInvoices = clientInvRes.data?.data || [];
            const vendorInvoices = vendorInvRes.data?.data || [];
            const materials = matRes.data?.purchases || [];
            const pettyLogs = pettyRes.data?.logs || [];
            const projects = projRes.data?.projects || projectsList;

            const isAllProjects = selectedProjects.length === 0;
            const selectedSet = new Set(selectedProjects.map(String));

            // Determine which projects to analyze for workforce
            const targetProjects = projects.filter(p => isAllProjects || selectedSet.has(String(p.id)));

            // 2. Fetch Labour & Salary data from endpoints:
            // A. get_project_profitabilitynew for each target project (has attendance worker rows)
            // B. get_monthly_payouts for months in duration
            // C. get_site_cost_analysis for months in duration
            const monthsToQuery = [];
            const nowD = new Date();
            const countMonths = duration === '1month' ? 2 : duration === '3months' ? 4 : duration === '6months' ? 7 : 12;
            for (let i = 0; i < countMonths; i++) {
                const mDate = new Date(nowD.getFullYear(), nowD.getMonth() - i, 1);
                monthsToQuery.push(mDate.toISOString().slice(0, 7));
            }

            const [profitabilityResults, monthlyPayoutResults, siteCostResults] = await Promise.all([
                Promise.all(targetProjects.map(p =>
                    api.get(`get_project_profitabilitynew.php?project_id=${p.id}`).catch(() => null)
                )),
                Promise.all(monthsToQuery.map(m =>
                    api.get(`get_monthly_payouts.php?month=${m}`).catch(() => null)
                )),
                Promise.all(monthsToQuery.map(m =>
                    api.get(`get_site_cost_analysis.php?month=${m}`).catch(() => null)
                ))
            ]);

            // ==========================================
            // COMPUTE TOTAL SALARY FOR EACH EMPLOYEE FOR SELECTED RANGE & PROJECT
            // ==========================================
            const employeeSalaryMap = {}; // Key: `${empName}___${projectName}`

            // A. Process labour_details from project profitability
            profitabilityResults.forEach((res, pIdx) => {
                const proj = targetProjects[pIdx];
                if (!proj || !res?.data?.details) return;
                const details = res.data.details;
                const labourList = details.labour_details || [];

                labourList.forEach(row => {
                    const rowDate = row.date || endDate;
                    if (rowDate < startDate || rowDate > endDate) return;

                    const empName = (row.description || 'Worker').trim();
                    const amt = parseFloat(row.amount || 0);
                    if (amt <= 0) return;

                    const key = `${empName}___${proj.project_name}`;
                    if (!employeeSalaryMap[key]) {
                        employeeSalaryMap[key] = {
                            id: `sal_${key}`,
                            employee_name: empName,
                            project_id: proj.id,
                            project_name: proj.project_name,
                            pay_type: 'Daily Wage',
                            total_salary: 0,
                            days_count: 0,
                            dates: []
                        };
                    }
                    employeeSalaryMap[key].total_salary += amt;
                    employeeSalaryMap[key].days_count += 1;
                    employeeSalaryMap[key].dates.push(rowDate);
                });

                // Fallback: If labour_details was empty but details.costs has Labour > 0
                const labourCostObj = details.costs?.find(c => c.name === 'Labour');
                if (labourCostObj && labourCostObj.value > 0 && labourList.length === 0) {
                    const key = `Workforce___${proj.project_name}`;
                    if (!employeeSalaryMap[key]) {
                        employeeSalaryMap[key] = {
                            id: `sal_${key}`,
                            employee_name: `Workforce (${proj.project_name})`,
                            project_id: proj.id,
                            project_name: proj.project_name,
                            pay_type: 'Project Wages',
                            total_salary: parseFloat(labourCostObj.value || 0),
                            days_count: 1,
                            dates: [endDate]
                        };
                    }
                }
            });

            // B. Process get_monthly_payouts (for monthly staff & site breakdowns)
            monthlyPayoutResults.forEach(res => {
                const payouts = res?.data?.payouts || [];
                payouts.forEach(p => {
                    const empName = (p.employee_name || 'Staff').trim();
                    const pType = p.pay_type || 'Monthly';
                    const sites = p.site_breakdown || [];

                    if (sites.length > 0) {
                        sites.forEach(st => {
                            const siteName = st.project_name;
                            // Check if site matches selected project(s)
                            if (!isAllProjects && !targetProjects.some(tp => tp.project_name.toLowerCase() === siteName.toLowerCase())) {
                                return;
                            }
                            const cost = parseFloat(st.cost || 0);
                            if (cost <= 0) return;

                            const key = `${empName}___${siteName}`;
                            if (!employeeSalaryMap[key]) {
                                employeeSalaryMap[key] = {
                                    id: `sal_${key}`,
                                    employee_name: empName,
                                    project_id: null,
                                    project_name: siteName,
                                    pay_type: pType,
                                    total_salary: 0,
                                    days_count: 0,
                                    workload: p.days_present || '1 Month',
                                    dates: []
                                };
                            }
                            // Only add if not already captured from direct attendance
                            if (employeeSalaryMap[key].days_count === 0) {
                                employeeSalaryMap[key].total_salary += cost;
                            }
                        });
                    } else if (isAllProjects) {
                        // General payroll
                        const earned = parseFloat(p.total_earned || p.base_earned || 0);
                        if (earned > 0) {
                            const key = `${empName}___Company Payroll`;
                            if (!employeeSalaryMap[key]) {
                                employeeSalaryMap[key] = {
                                    id: `sal_${key}`,
                                    employee_name: empName,
                                    project_id: null,
                                    project_name: 'Company Payroll',
                                    pay_type: pType,
                                    total_salary: earned,
                                    days_count: 1,
                                    workload: p.days_present || '1 Month',
                                    dates: []
                                };
                            }
                        }
                    }
                });
            });

            // C. Process site cost analysis as safeguard
            siteCostResults.forEach(res => {
                const siteData = res?.data?.data || [];
                siteData.forEach(item => {
                    const pId = String(item.project_id);
                    if (!isAllProjects && !selectedSet.has(pId)) return;
                    const cost = parseFloat(item.total_labor_cost || 0);
                    if (cost <= 0) return;

                    const hasProjectEntry = Object.values(employeeSalaryMap).some(e =>
                        e.project_id == item.project_id || e.project_name.toLowerCase() === item.project_name.toLowerCase()
                    );
                    if (!hasProjectEntry) {
                        const key = `Workforce___${item.project_name}`;
                        employeeSalaryMap[key] = {
                            id: `sal_${key}`,
                            employee_name: `Workforce (${item.unique_workers || 1} Workers)`,
                            project_id: item.project_id,
                            project_name: item.project_name,
                            pay_type: 'Attendance Wage',
                            total_salary: cost,
                            days_count: item.man_days || 1,
                            workload: item.total_hours > 0 ? `${item.total_hours.toFixed(1)} Hrs` : `${item.man_days || 1} Man-Days`,
                            dates: [endDate]
                        };
                    }
                });
            });

            // Transform employeeSalaryMap into employeeSalaryList
            const employeeSalaryList = Object.values(employeeSalaryMap).map(item => {
                const totSal = Math.round(item.total_salary);
                const workloadStr = item.workload || (item.days_count > 0 ? `${item.days_count} Shifts / Days` : 'Period Allocation');
                const appliedRate = item.days_count > 0 ? Math.round(totSal / item.days_count) : totSal;
                const latestDate = item.dates && item.dates.length > 0 ? item.dates[item.dates.length - 1] : endDate;
                return {
                    id: item.id,
                    type: 'Total Salary',
                    date: latestDate,
                    employee_id: item.employee_id || 0,
                    employee_name: item.employee_name,
                    pay_type: item.pay_type,
                    applied_rate: appliedRate,
                    workload: workloadStr,
                    amount: totSal,
                    project_id: item.project_id,
                    project_name: item.project_name,
                    notes: `Total salary for selected period on ${item.project_name}`
                };
            });

            const totalLabourCost = employeeSalaryList.reduce((sum, item) => sum + item.amount, 0);

            // ==========================================
            // PROCESS INCOME & OTHER EXPENSES
            // ==========================================
            const incomeList = [];
            let totalIncome = 0;
            clientInvoices.forEach(inv => {
                const pId = inv.project_id ? String(inv.project_id) : '';
                if (!isAllProjects && !selectedSet.has(pId)) return;
                const payDate = inv.invoice_date || inv.created_at || endDate;
                if (payDate < startDate || payDate > endDate) return;

                const recAmt = parseFloat(inv.paid_amount || inv.total_amount || 0);
                if (recAmt > 0) {
                    totalIncome += recAmt;
                    incomeList.push({
                        id: `ci_${inv.id}`,
                        amount: recAmt,
                        tds_deducted: 0,
                        payment_date: payDate,
                        payment_mode: 'Bank Transfer',
                        transaction_ref: inv.invoice_no,
                        notes: `Client Billing - ${inv.client_name || 'Client'}`,
                        receipt_copy_path: null,
                        invoice_no: inv.invoice_no,
                        client_name: inv.client_name || 'Client',
                        project_id: inv.project_id,
                        project_name: inv.project_name || 'General Project'
                    });
                }
            });

            const vendorList = [];
            let totalVendor = 0;
            vendorInvoices.forEach(vi => {
                const pId = vi.project_id ? String(vi.project_id) : '';
                if (!isAllProjects && !selectedSet.has(pId)) return;
                const vDate = vi.invoice_date || vi.created_at || endDate;
                if (vDate < startDate || vDate > endDate) return;

                const amt = parseFloat(vi.total_amount || vi.amount || 0);
                if (amt > 0) {
                    totalVendor += amt;
                    vendorList.push({
                        id: `vi_${vi.id}`,
                        payment_date: vDate,
                        amount: amt,
                        payment_method: 'NEFT / RTGS',
                        notes: `Vendor Bill #${vi.invoice_no}`,
                        attachment_path: null,
                        invoice_no: vi.invoice_no,
                        vendor_name: vi.vendor_name || 'Vendor Partner',
                        project_id: vi.project_id,
                        project_name: vi.project_name || 'General Project'
                    });
                }
            });

            const materialList = [];
            let totalMaterial = 0;
            materials.forEach(mp => {
                const pId = mp.project_id ? String(mp.project_id) : '';
                if (!isAllProjects && !selectedSet.has(pId)) return;
                const mDate = mp.purchase_date || mp.created_at || endDate;
                if (mDate < startDate || mDate > endDate) return;

                const amt = parseFloat(mp.amount || 0);
                if (amt > 0) {
                    totalMaterial += amt;
                    materialList.push({
                        id: `mp_${mp.id}`,
                        date: mDate,
                        amount: amt,
                        biller_name: mp.biller_name || 'Local Supplier',
                        items_summary: mp.items_summary || 'Site materials',
                        paid_by: mp.paid_by || 'Staff',
                        payment_method: mp.payment_method || 'Direct',
                        status: mp.status || 'Verified',
                        bill_screenshot: mp.bill_screenshot || null,
                        project_id: mp.project_id,
                        project_name: mp.project_name || 'Direct Procurement'
                    });
                }
            });

            const pettyList = [];
            let totalPetty = 0;
            pettyLogs.forEach(pc => {
                const pId = pc.project_id ? String(pc.project_id) : '';
                if (!isAllProjects && !selectedSet.has(pId)) return;
                const pDate = pc.date || pc.created_at || endDate;
                if (pDate < startDate || pDate > endDate) return;

                const amt = parseFloat(pc.amount || 0);
                if (amt > 0 && pc.type === 'debit') {
                    totalPetty += amt;
                    pettyList.push({
                        id: `pc_${pc.id}`,
                        date: pDate,
                        amount: amt,
                        category: pc.category || 'General Site',
                        remarks: pc.remarks || pc.description || 'Petty spend',
                        status: pc.status || 'Approved',
                        bill_image: pc.bill_image || null,
                        staff_name: pc.staff_name || 'Site Supervisor',
                        project_id: pc.project_id,
                        project_name: pc.project_name || 'Site Operations'
                    });
                }
            });

            // Build Unified All Expenses Stream (Including Employee Salaries)
            const allExpensesList = [];

            // Add Employee Total Salaries
            employeeSalaryList.forEach(em => {
                allExpensesList.push({
                    id: em.id,
                    category: 'Employee / Labour',
                    date: em.date,
                    entity_name: em.employee_name,
                    project_id: em.project_id,
                    project_name: em.project_name,
                    amount: em.amount,
                    payment_method: em.pay_type,
                    description: `Total Salary (${em.workload}) on ${em.project_name}`,
                    proof_url: null
                });
            });

            vendorList.forEach(v => {
                allExpensesList.push({
                    id: v.id,
                    category: 'Vendor Payment',
                    date: v.payment_date,
                    entity_name: v.vendor_name,
                    project_id: v.project_id,
                    project_name: v.project_name,
                    amount: v.amount,
                    payment_method: v.payment_method,
                    description: v.notes,
                    proof_url: v.attachment_path
                });
            });

            pettyList.forEach(p => {
                allExpensesList.push({
                    id: p.id,
                    category: 'Petty Cash',
                    date: p.date,
                    entity_name: p.staff_name,
                    project_id: p.project_id,
                    project_name: p.project_name,
                    amount: p.amount,
                    payment_method: 'Cash',
                    description: `[${p.category}] ${p.remarks}`,
                    proof_url: p.bill_image
                });
            });

            materialList.forEach(m => {
                allExpensesList.push({
                    id: m.id,
                    category: 'Material Spent',
                    date: m.date,
                    entity_name: m.biller_name,
                    project_id: m.project_id,
                    project_name: m.project_name,
                    amount: m.amount,
                    payment_method: m.payment_method,
                    description: m.items_summary,
                    proof_url: m.bill_screenshot
                });
            });

            allExpensesList.sort((a, b) => (b.date || '').localeCompare(a.date || ''));

            // Calculations
            const totalExpenses = totalLabourCost + totalVendor + totalPetty + totalMaterial;
            const netProfit = totalIncome - totalExpenses;
            const margin = totalIncome > 0 ? ((netProfit / totalIncome) * 100).toFixed(1) : 0;

            // Monthly Timeline
            const timelineMap = {};
            incomeList.forEach(item => {
                const mKey = (item.payment_date || '').slice(0, 7) || 'Current';
                if (!timelineMap[mKey]) timelineMap[mKey] = { month: mKey, income: 0, expenses: 0 };
                timelineMap[mKey].income += item.amount;
            });
            allExpensesList.forEach(item => {
                const mKey = (item.date || '').slice(0, 7) || 'Current';
                if (!timelineMap[mKey]) timelineMap[mKey] = { month: mKey, income: 0, expenses: 0 };
                timelineMap[mKey].expenses += item.amount;
            });
            const timeline = Object.values(timelineMap).sort((a, b) => a.month.localeCompare(b.month));

            // Project Breakdown
            const projMap = {};
            projects.forEach(p => {
                const pId = String(p.id);
                if (!isAllProjects && !selectedSet.has(pId)) return;
                projMap[pId] = {
                    project_id: p.id,
                    project_name: p.project_name,
                    total_budget: parseFloat(p.total_budget || 0),
                    income: 0,
                    labour_expense: 0,
                    vendor_expense: 0,
                    petty_expense: 0,
                    material_expense: 0,
                    total_expense: 0,
                    net_profit: 0
                };
            });

            incomeList.forEach(inc => {
                const pId = String(inc.project_id);
                if (projMap[pId]) projMap[pId].income += inc.amount;
            });
            employeeSalaryList.forEach(em => {
                const pId = String(em.project_id);
                if (projMap[pId]) {
                    projMap[pId].labour_expense += em.amount;
                } else {
                    const matchEntry = Object.values(projMap).find(p => p.project_name.toLowerCase() === em.project_name.toLowerCase());
                    if (matchEntry) matchEntry.labour_expense += em.amount;
                }
            });
            vendorList.forEach(ven => {
                const pId = String(ven.project_id);
                if (projMap[pId]) projMap[pId].vendor_expense += ven.amount;
            });
            pettyList.forEach(pet => {
                const pId = String(pet.project_id);
                if (projMap[pId]) projMap[pId].petty_expense += pet.amount;
            });
            materialList.forEach(mat => {
                const pId = String(mat.project_id);
                if (projMap[pId]) projMap[pId].material_expense += mat.amount;
            });

            const project_breakdown = Object.values(projMap).map(p => {
                p.total_expense = p.labour_expense + p.vendor_expense + p.petty_expense + p.material_expense;
                p.net_profit = p.income - p.total_expense;
                p.margin = p.income > 0 ? Math.round((p.net_profit / p.income) * 100) : 0;
                return p;
            });

            setFinancialData({
                summary: {
                    total_income: Math.round(totalIncome),
                    total_expenses: Math.round(totalExpenses),
                    net_profit: Math.round(netProfit),
                    profit_margin: parseFloat(margin),
                    breakdown: {
                        employee: Math.round(totalLabourCost),
                        vendor: Math.round(totalVendor),
                        petty_cash: Math.round(totalPetty),
                        material: Math.round(totalMaterial)
                    },
                    counts: {
                        income_transactions: incomeList.length,
                        total_expense_transactions: allExpensesList.length,
                        employee_records: employeeSalaryList.length,
                        vendor_payments: vendorList.length,
                        petty_records: pettyList.length,
                        material_purchases: materialList.length
                    }
                },
                timeline,
                project_breakdown,
                income: incomeList,
                all_expenses: allExpensesList,
                employee_expenses: employeeSalaryList,
                vendor_payments: vendorList,
                petty_cash: pettyList,
                material_spent: materialList
            });
        } catch (fallbackErr) {
            console.error("Fallback aggregator error:", fallbackErr);
        }
    };

    // Primary fetch function
    const fetchFinancialBreakdown = async () => {
        setLoading(true);
        try {
            const projectQuery = selectedProjects.length === 0 ? 'all' : selectedProjects.join(',');
            const res = await api.get(`get_financial_breakdown.php?duration=${duration}&projects=${projectQuery}`);

            if (res.data && res.data.status === 'success') {
                setFinancialData(res.data);
                if (res.data.projects_list && res.data.projects_list.length > 0) {
                    setProjectsList(res.data.projects_list);
                }
            } else {
                await fallbackAggregateData();
            }
        } catch (err) {
            console.warn("Dedicated endpoint unreachable, running fallback aggregator:", err);
            await fallbackAggregateData();
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchFinancialBreakdown();
    };

    // Project selection handlers
    const toggleProject = (projectId) => {
        setSelectedProjects(prev => {
            if (prev.includes(projectId)) {
                return prev.filter(id => id !== projectId);
            } else {
                return [...prev, projectId];
            }
        });
    };

    const selectAllProjects = () => {
        setSelectedProjects([]);
    };

    const clearAllProjects = () => {
        setSelectedProjects([]);
    };

    // Filter project items in dropdown
    const filteredDropdownProjects = useMemo(() => {
        return projectsList.filter(p =>
            (p.project_name || '').toLowerCase().includes(projectSearchQuery.toLowerCase())
        );
    }, [projectsList, projectSearchQuery]);

    // Currency formatting helpers
    const formatINR = (val) => {
        const num = Number(val) || 0;
        return `₹${num.toLocaleString('en-IN')}`;
    };

    const formatLakh = (val) => {
        const num = Number(val) || 0;
        if (Math.abs(num) >= 100000) {
            return `₹${(num / 100000).toFixed(2)}L`;
        }
        return `₹${num.toLocaleString('en-IN')}`;
    };

    // Prepare Cost Breakdown for Recharts Donut Pie
    const pieCostData = useMemo(() => {
        const bd = financialData.summary?.breakdown || {};
        const items = [
            { name: 'Employee / Labour', value: bd.employee || 0, color: COST_COLORS.employee },
            { name: 'Vendor Payments', value: bd.vendor || 0, color: COST_COLORS.vendor },
            { name: 'Petty Cash', value: bd.petty_cash || 0, color: COST_COLORS.petty_cash },
            { name: 'Material Spent', value: bd.material || 0, color: COST_COLORS.material },
        ];
        return items.filter(i => i.value > 0);
    }, [financialData.summary]);

    // Export entire financial ledger to Excel
    const handleExportExcel = () => {
        try {
            const wb = XLSX.utils.book_new();

            // 1. Summary Sheet
            const summarySheetData = [
                { Metric: 'Report Duration', Value: duration.toUpperCase() },
                { Metric: 'Date Interval', Value: dateRangeLabel },
                { Metric: 'Selected Projects', Value: selectedProjects.length === 0 ? 'All Projects' : `${selectedProjects.length} Selected` },
                { Metric: 'Gross Income (Inflow)', Value: financialData.summary.total_income },
                { Metric: 'Total Operational Expenses (Outflow)', Value: financialData.summary.total_expenses },
                { Metric: 'Net Profit / Cash Flow', Value: financialData.summary.net_profit },
                { Metric: 'Net Profit Margin (%)', Value: `${financialData.summary.profit_margin}%` },
                { Metric: 'Employee / Labour Wages', Value: financialData.summary.breakdown?.employee || 0 },
                { Metric: 'Vendor Payments', Value: financialData.summary.breakdown?.vendor || 0 },
                { Metric: 'Petty Cash Outflow', Value: financialData.summary.breakdown?.petty_cash || 0 },
                { Metric: 'Material Procurement', Value: financialData.summary.breakdown?.material || 0 }
            ];
            const wsSummary = XLSX.utils.json_to_sheet(summarySheetData);
            XLSX.utils.book_append_sheet(wb, wsSummary, 'Executive Summary');

            // 2. Income Sheet
            if (financialData.income && financialData.income.length > 0) {
                const incData = financialData.income.map(i => ({
                    'Date': i.payment_date,
                    'Invoice No': i.invoice_no,
                    'Client Name': i.client_name,
                    'Project Name': i.project_name,
                    'Payment Mode': i.payment_mode,
                    'Reference': i.transaction_ref || '',
                    'Amount (INR)': i.amount,
                    'TDS Deducted': i.tds_deducted || 0,
                    'Notes': i.notes || ''
                }));
                const wsIncome = XLSX.utils.json_to_sheet(incData);
                XLSX.utils.book_append_sheet(wb, wsIncome, 'Income (Inflows)');
            }

            // 3. Consolidated Expenses Sheet
            if (financialData.all_expenses && financialData.all_expenses.length > 0) {
                const expData = financialData.all_expenses.map(e => ({
                    'Category': e.category,
                    'Date': e.date,
                    'Payee / Entity': e.entity_name,
                    'Project': e.project_name,
                    'Payment Method': e.payment_method,
                    'Description': e.description,
                    'Amount (INR)': e.amount
                }));
                const wsExp = XLSX.utils.json_to_sheet(expData);
                XLSX.utils.book_append_sheet(wb, wsExp, 'All Expenses');
            }

            // 4. Employee / Labour Sheet
            if (financialData.employee_expenses && financialData.employee_expenses.length > 0) {
                const empData = financialData.employee_expenses.map(em => ({
                    'Employee / Worker Name': em.employee_name,
                    'Project Name': em.project_name,
                    'Pay Type': em.pay_type,
                    'Workload / Days Worked': em.workload,
                    'Applied Daily/Base Rate': em.applied_rate,
                    'Total Salary (INR)': em.amount,
                    'Notes': em.notes || ''
                }));
                const wsEmp = XLSX.utils.json_to_sheet(empData);
                XLSX.utils.book_append_sheet(wb, wsEmp, 'Employee Labour');
            }

            // 5. Vendor Payments Sheet
            if (financialData.vendor_payments && financialData.vendor_payments.length > 0) {
                const venData = financialData.vendor_payments.map(v => ({
                    'Date': v.payment_date,
                    'Vendor Name': v.vendor_name,
                    'Invoice No': v.invoice_no,
                    'Project': v.project_name,
                    'Mode': v.payment_method,
                    'Amount (INR)': v.amount,
                    'Notes': v.notes || ''
                }));
                const wsVen = XLSX.utils.json_to_sheet(venData);
                XLSX.utils.book_append_sheet(wb, wsVen, 'Vendor Payments');
            }

            // 6. Petty Cash Sheet
            if (financialData.petty_cash && financialData.petty_cash.length > 0) {
                const petData = financialData.petty_cash.map(p => ({
                    'Date': p.date,
                    'Category': p.category,
                    'Remarks': p.remarks,
                    'Staff': p.staff_name,
                    'Project': p.project_name,
                    'Amount (INR)': p.amount,
                    'Status': p.status
                }));
                const wsPet = XLSX.utils.json_to_sheet(petData);
                XLSX.utils.book_append_sheet(wb, wsPet, 'Petty Cash');
            }

            // 7. Material Spent Sheet
            if (financialData.material_spent && financialData.material_spent.length > 0) {
                const matData = financialData.material_spent.map(m => ({
                    'Date': m.date,
                    'Supplier / Biller': m.biller_name,
                    'Items Description': m.items_summary,
                    'Project': m.project_name,
                    'Payment Method': m.payment_method,
                    'Paid By': m.paid_by,
                    'Amount (INR)': m.amount
                }));
                const wsMat = XLSX.utils.json_to_sheet(matData);
                XLSX.utils.book_append_sheet(wb, wsMat, 'Material Spent');
            }

            XLSX.writeFile(wb, `Financial_Breakdown_${duration}_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error("Failed to export excel ledger:", error);
            alert("Error exporting Excel. Please check console.");
        }
    };

    // Print helper
    const handlePrint = () => {
        window.print();
    };

    // Filter tables by active search query
    const filterTableRows = (rows, searchFields) => {
        if (!tableSearch.trim()) return rows;
        const q = tableSearch.toLowerCase();
        return rows.filter(r =>
            searchFields.some(field => {
                const val = r[field];
                return val && String(val).toLowerCase().includes(q);
            })
        );
    };

    const filteredIncome = useMemo(() =>
        filterTableRows(financialData.income || [], ['client_name', 'project_name', 'invoice_no', 'payment_mode', 'transaction_ref', 'notes']),
        [financialData.income, tableSearch]
    );

    const filteredAllExpenses = useMemo(() =>
        filterTableRows(financialData.all_expenses || [], ['category', 'entity_name', 'project_name', 'description', 'payment_method']),
        [financialData.all_expenses, tableSearch]
    );

    const filteredEmployeeExpenses = useMemo(() =>
        filterTableRows(financialData.employee_expenses || [], ['employee_name', 'project_name', 'pay_type', 'type', 'workload', 'notes']),
        [financialData.employee_expenses, tableSearch]
    );

    const filteredVendorPayments = useMemo(() =>
        filterTableRows(financialData.vendor_payments || [], ['vendor_name', 'project_name', 'invoice_no', 'payment_method', 'notes']),
        [financialData.vendor_payments, tableSearch]
    );

    const filteredPettyCash = useMemo(() =>
        filterTableRows(financialData.petty_cash || [], ['category', 'remarks', 'staff_name', 'project_name', 'status']),
        [financialData.petty_cash, tableSearch]
    );

    const filteredMaterialSpent = useMemo(() =>
        filterTableRows(financialData.material_spent || [], ['biller_name', 'items_summary', 'project_name', 'paid_by', 'payment_method']),
        [financialData.material_spent, tableSearch]
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-500 pb-24 print:p-0 print:space-y-4">
            {/* =========================================================================
                1. TOP HEADER & INTERACTIVE FILTER CONTROLS
            ========================================================================= */}
            <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm print:hidden">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    {/* Title & Metadata */}
                    <div>
                        <div className="flex items-center gap-2 text-indigo-600 mb-1">
                            <span className="p-2 bg-indigo-50 rounded-xl text-indigo-600">
                                <Banknote size={20} />
                            </span>
                            <span className="text-[11px] font-black uppercase tracking-[0.2em]">
                                Financial Intelligence & Multi-Project Audit
                            </span>
                        </div>
                        <h1 className="text-3xl lg:text-4xl font-black text-slate-900 tracking-tight italic">
                            Financial Breakdown
                        </h1>
                        <p className="text-slate-400 font-bold text-xs uppercase tracking-wider mt-1 flex items-center gap-2">
                            <span>{user?.company_name || 'ClientOrbit Pro'}</span>
                            <span>•</span>
                            <span className="text-indigo-600 font-black">{dateRangeLabel}</span>
                        </p>
                    </div>

                    {/* Duration Pills + Project Filter + Action Buttons */}
                    <div className="flex flex-wrap items-center gap-3">
                        {/* DURATION PILLS */}
                        <div className="flex items-center bg-slate-100 p-1.5 rounded-2xl border border-slate-200/60 shadow-inner">
                            {[
                                { key: '1month', label: '1 Month' },
                                { key: '3months', label: '3 Months' },
                                { key: '6months', label: '6 Months' },
                                { key: 'overall', label: 'Overall' },
                            ].map(item => (
                                <button
                                    key={item.key}
                                    onClick={() => setDuration(item.key)}
                                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition-all duration-200 ${
                                        duration === item.key
                                            ? 'bg-slate-900 text-white shadow-md shadow-slate-900/20 scale-[1.02]'
                                            : 'text-slate-500 hover:text-slate-900 hover:bg-white/60'
                                    }`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        {/* MULTI-PROJECT SELECTOR DROPDOWN */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                                className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-black uppercase transition-all border ${
                                    selectedProjects.length > 0
                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300 shadow-sm'
                                }`}
                            >
                                <Building2 size={16} />
                                <span>
                                    {selectedProjects.length === 0
                                        ? 'All Projects'
                                        : `${selectedProjects.length} Selected`}
                                </span>
                                <ChevronDown size={14} className={`transition-transform duration-200 ${isProjectDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown Popover */}
                            {isProjectDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-100 rounded-3xl shadow-2xl p-4 z-50 animate-in fade-in zoom-in-95 duration-150">
                                    <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-3">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                            Select Projects
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={selectAllProjects}
                                                className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-lg transition-all ${
                                                    selectedProjects.length === 0
                                                        ? 'bg-indigo-100 text-indigo-700 font-bold'
                                                        : 'text-slate-500 hover:text-indigo-600'
                                                }`}
                                            >
                                                All
                                            </button>
                                            {selectedProjects.length > 0 && (
                                                <button
                                                    onClick={clearAllProjects}
                                                    className="text-[10px] font-black uppercase text-rose-500 hover:text-rose-700"
                                                >
                                                    Reset
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Search Inside Dropdown */}
                                    <div className="relative mb-3">
                                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search projects..."
                                            value={projectSearchQuery}
                                            onChange={(e) => setProjectSearchQuery(e.target.value)}
                                            className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2 pl-9 pr-3 text-xs font-semibold outline-none focus:bg-white focus:border-indigo-300 transition-all"
                                        />
                                    </div>

                                    {/* Projects Checkbox List */}
                                    <div className="max-h-60 overflow-y-auto space-y-1 pr-1">
                                        {filteredDropdownProjects.length === 0 ? (
                                            <p className="text-center text-xs text-slate-400 py-4 italic">No projects found</p>
                                        ) : (
                                            filteredDropdownProjects.map(proj => {
                                                const isChecked = selectedProjects.includes(proj.id);
                                                return (
                                                    <label
                                                        key={proj.id}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            toggleProject(proj.id);
                                                        }}
                                                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all ${
                                                            isChecked
                                                                ? 'bg-indigo-50 text-indigo-700'
                                                                : 'hover:bg-slate-50 text-slate-600'
                                                        }`}
                                                    >
                                                        <div className={`w-4 h-4 rounded-md border flex items-center justify-center transition-all ${
                                                            isChecked
                                                                ? 'bg-indigo-600 border-indigo-600 text-white'
                                                                : 'border-slate-300 bg-white'
                                                        }`}>
                                                            {isChecked && <Check size={12} strokeWidth={3} />}
                                                        </div>
                                                        <span className="truncate flex-1">{proj.project_name}</span>
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>

                                    {/* Footer count */}
                                    <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] font-bold text-slate-400 flex justify-between">
                                        <span>Total: {projectsList.length} sites</span>
                                        <span className="text-indigo-600">{selectedProjects.length === 0 ? 'Showing All' : `${selectedProjects.length} active`}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* REFRESH & EXPORT ACTIONS */}
                        <button
                            onClick={handleRefresh}
                            disabled={loading || refreshing}
                            title="Refresh Data"
                            className="p-3 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-2xl shadow-sm hover:bg-slate-50 transition-all disabled:opacity-50"
                        >
                            <RefreshCw size={16} className={refreshing || loading ? 'animate-spin text-indigo-600' : ''} />
                        </button>

                        <button
                            onClick={handleExportExcel}
                            title="Export to Excel"
                            className="flex items-center gap-2 px-4 py-3 bg-white border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-emerald-700 font-black text-xs uppercase rounded-2xl shadow-sm transition-all"
                        >
                            <Download size={16} />
                            <span className="hidden sm:inline">Excel</span>
                        </button>

                        <button
                            onClick={handlePrint}
                            title="Print Statement"
                            className="flex items-center gap-2 px-4 py-3 bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase rounded-2xl shadow-lg shadow-slate-900/10 transition-all"
                        >
                            <Printer size={16} />
                            <span className="hidden sm:inline">Print</span>
                        </button>
                    </div>
                </div>

                {/* ACTIVE PROJECT FILTER CHIPS */}
                {selectedProjects.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 mr-1">
                            Active Filters ({selectedProjects.length}):
                        </span>
                        {selectedProjects.map(pId => {
                            const pObj = projectsList.find(p => p.id === pId);
                            return (
                                <span
                                    key={pId}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-full text-xs font-black uppercase"
                                >
                                    <span>{pObj ? pObj.project_name : `Project #${pId}`}</span>
                                    <button
                                        onClick={() => toggleProject(pId)}
                                        className="hover:text-rose-600 transition-colors"
                                    >
                                        <X size={12} />
                                    </button>
                                </span>
                            );
                        })}
                        <button
                            onClick={clearAllProjects}
                            className="text-[10px] font-black uppercase text-rose-500 hover:underline ml-2"
                        >
                            Clear All
                        </button>
                    </div>
                )}
            </div>

            {/* =========================================================================
                2. REAL-TIME HERO KPI METRICS CARDS
            ========================================================================= */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* 1. GROSS INCOME */}
                <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">
                            Gross Inflow
                        </span>
                        <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <ArrowUpCircle size={20} />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <h3 className="text-3xl font-black text-slate-900 tracking-tight">
                            {formatINR(financialData.summary.total_income)}
                        </h3>
                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-wider flex items-center gap-1">
                            <TrendingUp size={12} />
                            <span>{financialData.summary.counts?.income_transactions || 0} Inflow Receipts</span>
                        </p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-50 flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase">
                        <span>Realized Revenue</span>
                        <span className="text-slate-900 font-black">{formatLakh(financialData.summary.total_income)}</span>
                    </div>
                </div>

                {/* 2. TOTAL EXPENSES (Outflow) */}
                <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">
                            Total Outflow
                        </span>
                        <div className="w-10 h-10 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <ArrowDownCircle size={20} />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <h3 className="text-3xl font-black text-slate-900 tracking-tight">
                            {formatINR(financialData.summary.total_expenses)}
                        </h3>
                        <p className="text-[10px] font-black text-rose-600 uppercase tracking-wider flex items-center gap-1">
                            <TrendingDown size={12} />
                            <span>{financialData.summary.counts?.total_expense_transactions || 0} Total Expenses</span>
                        </p>
                    </div>
                    {/* Mini Cost Breakdown Bar */}
                    <div className="mt-4 pt-4 border-t border-slate-50 space-y-1.5">
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden flex">
                            {financialData.summary.total_expenses > 0 && (
                                <>
                                    <div
                                        style={{ width: `${((financialData.summary.breakdown?.employee || 0) / financialData.summary.total_expenses) * 100}%`, backgroundColor: COST_COLORS.employee }}
                                        title="Labour Wages"
                                    />
                                    <div
                                        style={{ width: `${((financialData.summary.breakdown?.vendor || 0) / financialData.summary.total_expenses) * 100}%`, backgroundColor: COST_COLORS.vendor }}
                                        title="Vendor Bills"
                                    />
                                    <div
                                        style={{ width: `${((financialData.summary.breakdown?.petty_cash || 0) / financialData.summary.total_expenses) * 100}%`, backgroundColor: COST_COLORS.petty_cash }}
                                        title="Petty Cash"
                                    />
                                    <div
                                        style={{ width: `${((financialData.summary.breakdown?.material || 0) / financialData.summary.total_expenses) * 100}%`, backgroundColor: COST_COLORS.material }}
                                        title="Materials"
                                    />
                                </>
                            )}
                        </div>
                        <div className="flex justify-between text-[9px] font-black text-slate-400 uppercase">
                            <span>Labour: {formatLakh(financialData.summary.breakdown?.employee || 0)}</span>
                            <span className="text-slate-900">{formatLakh(financialData.summary.total_expenses)}</span>
                        </div>
                    </div>
                </div>

                {/* 3. NET PROFIT / NET CASH FLOW */}
                <div className={`rounded-[32px] p-6 shadow-sm transition-shadow relative overflow-hidden text-white ${
                    financialData.summary.net_profit >= 0
                        ? 'bg-gradient-to-br from-slate-900 to-slate-800'
                        : 'bg-gradient-to-br from-rose-950 to-slate-900'
                }`}>
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-[10px] font-black uppercase tracking-[0.15em] text-indigo-300">
                            Net Cash Flow
                        </span>
                        <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
                            <Target size={20} className={financialData.summary.net_profit >= 0 ? "text-emerald-400" : "text-rose-400"} />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <h3 className="text-3xl font-black tracking-tight italic">
                            {formatINR(financialData.summary.net_profit)}
                        </h3>
                        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                            {financialData.summary.net_profit >= 0 ? 'Operating Surplus' : 'Deficit Outflow'}
                        </p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center text-[10px] font-bold text-slate-300 uppercase">
                        <span>Net Bottom Line</span>
                        <span className={`font-black ${financialData.summary.net_profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {financialData.summary.net_profit >= 0 ? '+' : ''}{formatLakh(financialData.summary.net_profit)}
                        </span>
                    </div>
                </div>

                {/* 4. NET PROFIT MARGIN */}
                <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">
                            Profit Margin ROI
                        </span>
                        <div className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Wallet size={20} />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <h3 className={`text-3xl font-black tracking-tight ${financialData.summary.profit_margin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {financialData.summary.profit_margin}%
                        </h3>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                            Realized Margin
                        </p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-50 flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase">
                        <span>Margin Health</span>
                        <span className={`font-black px-2 py-0.5 rounded-full text-[9px] ${
                            financialData.summary.profit_margin >= 20
                                ? 'bg-emerald-100 text-emerald-700'
                                : financialData.summary.profit_margin >= 0
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-rose-100 text-rose-700'
                        }`}>
                            {financialData.summary.profit_margin >= 20 ? 'Optimal' : financialData.summary.profit_margin >= 0 ? 'Moderate' : 'Negative'}
                        </span>
                    </div>
                </div>
            </div>

            {/* =========================================================================
                3. TAB CONTROLS WITH RECORD COUNT BADGES & SEARCH
            ========================================================================= */}
            <div className="bg-white border border-slate-100 rounded-[32px] p-3 shadow-sm print:hidden">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Horizontal Scrollable Tabs */}
                    <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide py-1">
                        {[
                            { key: 'overview', label: 'Overview', icon: <Target size={15} /> },
                            { key: 'income', label: 'Income', icon: <TrendingUp size={15} />, count: financialData.summary.counts?.income_transactions },
                            { key: 'expenses', label: 'All Expenses', icon: <TrendingDown size={15} />, count: financialData.summary.counts?.total_expense_transactions },
                            { key: 'employee', label: 'Employee / Labour', icon: <HardHat size={15} />, count: financialData.summary.counts?.employee_records },
                            { key: 'vendor', label: 'Vendor Payments', icon: <Truck size={15} />, count: financialData.summary.counts?.vendor_payments },
                            { key: 'petty', label: 'Petty Cash', icon: <Receipt size={15} />, count: financialData.summary.counts?.petty_records },
                            { key: 'material', label: 'Material Spent', icon: <ShoppingCart size={15} />, count: financialData.summary.counts?.material_purchases },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => {
                                    setActiveTab(tab.key);
                                    setTableSearch('');
                                }}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black uppercase whitespace-nowrap transition-all ${
                                    activeTab === tab.key
                                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                                        : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                                }`}
                            >
                                <span>{tab.icon}</span>
                                <span>{tab.label}</span>
                                {typeof tab.count === 'number' && (
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                                        activeTab === tab.key
                                            ? 'bg-white/20 text-white'
                                            : 'bg-slate-100 text-slate-500'
                                    }`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Table Search input */}
                    {activeTab !== 'overview' && (
                        <div className="relative min-w-[240px]">
                            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder={`Search ${activeTab}...`}
                                value={tableSearch}
                                onChange={(e) => setTableSearch(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200/80 rounded-2xl py-2 pl-9 pr-8 text-xs font-bold outline-none focus:bg-white focus:border-indigo-300 transition-all"
                            />
                            {tableSearch && (
                                <button
                                    onClick={() => setTableSearch('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Loading Indicator */}
            {loading && (
                <div className="h-64 flex flex-col items-center justify-center text-slate-400 font-black italic uppercase tracking-widest bg-white rounded-[36px] border border-slate-100">
                    <Loader2 className="animate-spin mb-3 text-indigo-600" size={40} />
                    Calculating Financial Intelligence & Labour Salaries...
                </div>
            )}

            {/* =========================================================================
                4. TAB CONTENT RENDERERS
            ========================================================================= */}
            {!loading && (
                <>
                    {/* TAB 1: OVERVIEW */}
                    {activeTab === 'overview' && (
                        <div className="space-y-8 animate-in fade-in duration-300">
                            {/* 4 Expense Category Highlight Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[
                                    {
                                        title: 'Employee / Labour',
                                        subtitle: 'Workforce Total Salary',
                                        icon: <HardHat size={18} />,
                                        amount: financialData.summary.breakdown?.employee || 0,
                                        color: COST_COLORS.employee,
                                        bg: 'bg-purple-50',
                                        text: 'text-purple-700',
                                        tabKey: 'employee'
                                    },
                                    {
                                        title: 'Vendor Payments',
                                        subtitle: 'Vendor Bills & Invoices',
                                        icon: <Truck size={18} />,
                                        amount: financialData.summary.breakdown?.vendor || 0,
                                        color: COST_COLORS.vendor,
                                        bg: 'bg-indigo-50',
                                        text: 'text-indigo-700',
                                        tabKey: 'vendor'
                                    },
                                    {
                                        title: 'Petty Cash',
                                        subtitle: 'Approved Daily Site Debits',
                                        icon: <Receipt size={18} />,
                                        amount: financialData.summary.breakdown?.petty_cash || 0,
                                        color: COST_COLORS.petty_cash,
                                        bg: 'bg-emerald-50',
                                        text: 'text-emerald-700',
                                        tabKey: 'petty'
                                    },
                                    {
                                        title: 'Material Spent',
                                        subtitle: 'Direct Site Procurements',
                                        icon: <ShoppingCart size={18} />,
                                        amount: financialData.summary.breakdown?.material || 0,
                                        color: COST_COLORS.material,
                                        bg: 'bg-amber-50',
                                        text: 'text-amber-700',
                                        tabKey: 'material'
                                    }
                                ].map((cat, idx) => {
                                    const pct = financialData.summary.total_expenses > 0
                                        ? ((cat.amount / financialData.summary.total_expenses) * 100).toFixed(1)
                                        : 0;
                                    return (
                                        <div
                                            key={idx}
                                            onClick={() => setActiveTab(cat.tabKey)}
                                            className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer group"
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <div className={`p-3 rounded-2xl ${cat.bg} ${cat.text} group-hover:scale-110 transition-transform`}>
                                                    {cat.icon}
                                                </div>
                                                <span className="text-[10px] font-black px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 uppercase">
                                                    {pct}% of Cost
                                                </span>
                                            </div>
                                            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                                {cat.title}
                                            </p>
                                            <h4 className="text-2xl font-black text-slate-900 tracking-tight mt-1">
                                                {formatINR(cat.amount)}
                                            </h4>
                                            <p className="text-[10px] font-bold text-indigo-600 mt-2 flex items-center gap-1 group-hover:underline uppercase tracking-wider">
                                                <span>View Detailed Breakdown</span> →
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Charts Section: Monthly Cash Flow Timeline & Cost Distribution Donut */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2 bg-white rounded-[36px] p-8 border border-slate-100 shadow-sm">
                                    <div className="flex justify-between items-center mb-6">
                                        <div>
                                            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                                <TrendingUp size={18} className="text-indigo-600" />
                                                Cash Flow Timeline
                                            </h3>
                                            <p className="text-xs text-slate-400 font-bold uppercase mt-0.5">
                                                Inflow vs Outflow Across Selected Period
                                            </p>
                                        </div>
                                    </div>

                                    <div className="h-[320px] w-full">
                                        {financialData.timeline.length === 0 ? (
                                            <div className="h-full flex items-center justify-center text-slate-300 font-bold italic text-sm">
                                                No timeline data available for this range
                                            </div>
                                        ) : (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={financialData.timeline} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                                    <XAxis
                                                        dataKey="month"
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tick={{ fill: '#94A3B8', fontSize: 11, fontWeight: 'bold' }}
                                                        dy={8}
                                                    />
                                                    <YAxis
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tick={{ fill: '#94A3B8', fontSize: 10 }}
                                                        tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
                                                    />
                                                    <RechartsTooltip
                                                        cursor={{ fill: '#F8FAFF' }}
                                                        formatter={(value) => [formatINR(value), '']}
                                                        contentStyle={{
                                                            borderRadius: '20px',
                                                            border: 'none',
                                                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                                                            padding: '16px',
                                                            fontWeight: 'bold'
                                                        }}
                                                    />
                                                    <Legend
                                                        verticalAlign="top"
                                                        align="right"
                                                        iconType="circle"
                                                        wrapperStyle={{ paddingBottom: '20px', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase' }}
                                                    />
                                                    <Bar dataKey="income" name="Inflow (Income)" fill="#10B981" radius={[8, 8, 0, 0]} barSize={22} />
                                                    <Bar dataKey="expenses" name="Outflow (Expenses)" fill="#6366F1" radius={[8, 8, 0, 0]} barSize={22} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white rounded-[36px] p-8 border border-slate-100 shadow-sm flex flex-col justify-between">
                                    <div>
                                        <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                            <ShoppingCart size={18} className="text-amber-500" />
                                            Expense Allocation
                                        </h3>
                                        <p className="text-xs text-slate-400 font-bold uppercase mt-0.5">
                                            Cost Split Across 4 Buckets
                                        </p>
                                    </div>

                                    <div className="h-[240px] w-full my-4">
                                        {pieCostData.length === 0 ? (
                                            <div className="h-full flex items-center justify-center text-slate-300 font-bold italic text-sm">
                                                No expenses recorded
                                            </div>
                                        ) : (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={pieCostData}
                                                        innerRadius={65}
                                                        outerRadius={95}
                                                        paddingAngle={6}
                                                        dataKey="value"
                                                        stroke="none"
                                                    >
                                                        {pieCostData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <RechartsTooltip
                                                        formatter={(value) => [formatINR(value), '']}
                                                        contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 20px rgba(0,0,0,0.08)' }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        )}
                                    </div>

                                    <div className="space-y-2 border-t border-slate-50 pt-4">
                                        {pieCostData.map((item, idx) => {
                                            const pct = financialData.summary.total_expenses > 0
                                                ? ((item.value / financialData.summary.total_expenses) * 100).toFixed(0)
                                                : 0;
                                            return (
                                                <div key={idx} className="flex justify-between items-center text-xs">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                                                        <span className="font-bold text-slate-600">{item.name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-black text-slate-900">{formatINR(item.value)}</span>
                                                        <span className="text-[10px] font-black text-slate-400">({pct}%)</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Project-Wise Performance Matrix */}
                            {financialData.project_breakdown && financialData.project_breakdown.length > 0 && (
                                <div className="bg-white rounded-[36px] p-8 border border-slate-100 shadow-sm space-y-6">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                                <Building2 size={20} className="text-indigo-600" />
                                                Site-by-Site Profitability Matrix
                                            </h3>
                                            <p className="text-xs text-slate-400 font-bold uppercase mt-1">
                                                Aggregated Income, Cost Allocation & Net Margin per Project
                                            </p>
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-xs">
                                            <thead>
                                                <tr className="border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                                                    <th className="py-4 px-4">Project Name</th>
                                                    <th className="py-4 px-4 text-right">Income (Inflow)</th>
                                                    <th className="py-4 px-4 text-right">Labour (Salary)</th>
                                                    <th className="py-4 px-4 text-right">Vendor</th>
                                                    <th className="py-4 px-4 text-right">Petty Cash</th>
                                                    <th className="py-4 px-4 text-right">Material</th>
                                                    <th className="py-4 px-4 text-right">Total Outflow</th>
                                                    <th className="py-4 px-4 text-right">Net Margin</th>
                                                    <th className="py-4 px-4 text-center">ROI</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-50 font-bold">
                                                {financialData.project_breakdown.map((row, idx) => (
                                                    <tr key={idx} className="hover:bg-slate-50/70 transition-colors group">
                                                        <td className="py-4 px-4 font-black text-slate-900 flex items-center gap-2">
                                                            <div className="w-2 h-2 rounded-full bg-indigo-500" />
                                                            <span>{row.project_name}</span>
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-black text-emerald-600">
                                                            {formatINR(row.income)}
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-black text-purple-700">
                                                            {formatINR(row.labour_expense)}
                                                        </td>
                                                        <td className="py-4 px-4 text-right text-indigo-700">
                                                            {formatINR(row.vendor_expense)}
                                                        </td>
                                                        <td className="py-4 px-4 text-right text-emerald-700">
                                                            {formatINR(row.petty_expense)}
                                                        </td>
                                                        <td className="py-4 px-4 text-right text-amber-700">
                                                            {formatINR(row.material_expense)}
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-black text-rose-600">
                                                            {formatINR(row.total_expense)}
                                                        </td>
                                                        <td className={`py-4 px-4 text-right font-black ${
                                                            row.net_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'
                                                        }`}>
                                                            {formatINR(row.net_profit)}
                                                        </td>
                                                        <td className="py-4 px-4 text-center">
                                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                                                                row.margin >= 20
                                                                    ? 'bg-emerald-100 text-emerald-800'
                                                                    : row.margin >= 0
                                                                        ? 'bg-amber-100 text-amber-800'
                                                                        : 'bg-rose-100 text-rose-800'
                                                            }`}>
                                                                {row.margin}%
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 2: INCOME */}
                    {activeTab === 'income' && (
                        <div className="bg-white rounded-[36px] p-8 border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                        <TrendingUp size={20} className="text-emerald-600" />
                                        Client Receipts & Capital Inflows
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase mt-1">
                                        Showing {filteredIncome.length} payments received
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-slate-400 uppercase">Filtered Inflow Total</p>
                                    <p className="text-2xl font-black text-emerald-600">
                                        {formatINR(filteredIncome.reduce((acc, i) => acc + (Number(i.amount) || 0), 0))}
                                    </p>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                                            <th className="py-4 px-4">Date</th>
                                            <th className="py-4 px-4">Invoice #</th>
                                            <th className="py-4 px-4">Client Name</th>
                                            <th className="py-4 px-4">Project</th>
                                            <th className="py-4 px-4">Payment Mode</th>
                                            <th className="py-4 px-4">Reference / Notes</th>
                                            <th className="py-4 px-4 text-right">TDS</th>
                                            <th className="py-4 px-4 text-right">Received Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 font-bold">
                                        {filteredIncome.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="py-12 text-center text-slate-300 font-bold italic text-sm">
                                                    No income records found for this criteria
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredIncome.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                                                    <td className="py-4 px-4 text-slate-500 font-semibold">{row.payment_date}</td>
                                                    <td className="py-4 px-4 font-black text-indigo-600">{row.invoice_no || 'Direct'}</td>
                                                    <td className="py-4 px-4 text-slate-900 font-bold">{row.client_name}</td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-slate-700 font-black text-[10px] uppercase">
                                                            {row.project_name}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase">
                                                            {row.payment_mode || 'Cash'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-400 text-xs truncate max-w-[200px]">
                                                        {row.transaction_ref ? `${row.transaction_ref} • ` : ''}{row.notes || '—'}
                                                    </td>
                                                    <td className="py-4 px-4 text-right text-slate-400">
                                                        {row.tds_deducted > 0 ? formatINR(row.tds_deducted) : '—'}
                                                    </td>
                                                    <td className="py-4 px-4 text-right font-black text-emerald-600 text-sm">
                                                        {formatINR(row.amount)}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 3: ALL EXPENSES */}
                    {activeTab === 'expenses' && (
                        <div className="bg-white rounded-[36px] p-8 border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                        <TrendingDown size={20} className="text-rose-600" />
                                        Unified Expenses Stream
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase mt-1">
                                        Consolidated Outflows across Labour Salaries, Vendors, Petty Cash & Materials ({filteredAllExpenses.length} entries)
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-slate-400 uppercase">Filtered Outflow Total</p>
                                    <p className="text-2xl font-black text-rose-600">
                                        {formatINR(filteredAllExpenses.reduce((acc, e) => acc + (Number(e.amount) || 0), 0))}
                                    </p>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                                            <th className="py-4 px-4">Category</th>
                                            <th className="py-4 px-4">Date</th>
                                            <th className="py-4 px-4">Payee / Entity</th>
                                            <th className="py-4 px-4">Project</th>
                                            <th className="py-4 px-4">Mode</th>
                                            <th className="py-4 px-4">Description / Summary</th>
                                            <th className="py-4 px-4 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 font-bold">
                                        {filteredAllExpenses.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="py-12 text-center text-slate-300 font-bold italic text-sm">
                                                    No expense records found
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredAllExpenses.map((row, idx) => {
                                                const catPillColor =
                                                    row.category === 'Employee / Labour'
                                                        ? 'bg-purple-100 text-purple-800 font-black'
                                                        : row.category === 'Vendor Payment'
                                                            ? 'bg-indigo-100 text-indigo-800'
                                                            : row.category === 'Petty Cash'
                                                                ? 'bg-emerald-100 text-emerald-800'
                                                                : 'bg-amber-100 text-amber-800';

                                                return (
                                                    <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                                                        <td className="py-4 px-4">
                                                            <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase ${catPillColor}`}>
                                                                {row.category}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-4 text-slate-500 font-semibold">{row.date}</td>
                                                        <td className="py-4 px-4 text-slate-900 font-black">{row.entity_name}</td>
                                                        <td className="py-4 px-4">
                                                            <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-700 font-bold text-[10px]">
                                                                {row.project_name}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-4 text-slate-500 text-[10px] uppercase font-bold">{row.payment_method || 'Direct'}</td>
                                                        <td className="py-4 px-4 text-slate-500 max-w-[260px] truncate">{row.description || '—'}</td>
                                                        <td className="py-4 px-4 text-right font-black text-rose-600 text-sm">
                                                            {formatINR(row.amount)}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 4: EMPLOYEE / LABOUR (TOTAL SALARY FOR SELECTED RANGE & PROJECT) */}
                    {activeTab === 'employee' && (
                        <div className="bg-white rounded-[36px] p-8 border border-slate-100 shadow-sm space-y-8 animate-in fade-in duration-300">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-100 pb-6">
                                <div>
                                    <div className="flex items-center gap-2 text-purple-600 mb-1">
                                        <HardHat size={20} />
                                        <span className="text-[11px] font-black uppercase tracking-widest">
                                            Workforce Compensation Intelligence
                                        </span>
                                    </div>
                                    <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                                        Employee Salary Ledger
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase mt-1">
                                        Total salary for each employee for selected duration ({dateRangeLabel}) and projects
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-slate-400 uppercase">Total Labour / Salary Expense</p>
                                    <p className="text-3xl font-black text-purple-700 tracking-tight">
                                        {formatINR(filteredEmployeeExpenses.reduce((acc, em) => acc + (Number(em.amount) || 0), 0))}
                                    </p>
                                </div>
                            </div>

                            {/* Mini KPI Highlights for Employee Tab */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                <div className="bg-purple-50/60 border border-purple-100 rounded-3xl p-5">
                                    <p className="text-[10px] font-black uppercase tracking-wider text-purple-600 mb-1">Total Workforce</p>
                                    <p className="text-2xl font-black text-slate-900">{filteredEmployeeExpenses.length} Active Employees</p>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">On selected projects</p>
                                </div>
                                <div className="bg-slate-50 border border-slate-100 rounded-3xl p-5">
                                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Avg Salary / Worker</p>
                                    <p className="text-2xl font-black text-slate-900">
                                        {formatINR(filteredEmployeeExpenses.length > 0
                                            ? Math.round(filteredEmployeeExpenses.reduce((acc, em) => acc + (Number(em.amount) || 0), 0) / filteredEmployeeExpenses.length)
                                            : 0)}
                                    </p>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">For selected timeframe</p>
                                </div>
                                <div className="bg-indigo-50/60 border border-indigo-100 rounded-3xl p-5">
                                    <p className="text-[10px] font-black uppercase tracking-wider text-indigo-600 mb-1">Cost % of Outflow</p>
                                    <p className="text-2xl font-black text-indigo-700">
                                        {financialData.summary.total_expenses > 0
                                            ? ((financialData.summary.breakdown?.employee || 0) / financialData.summary.total_expenses * 100).toFixed(1)
                                            : 0}%
                                    </p>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">Of total company expenses</p>
                                </div>
                            </div>

                            {/* Main Employee Total Salary Table */}
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                                            <th className="py-4 px-4">Worker / Employee</th>
                                            <th className="py-4 px-4">Project Assigned</th>
                                            <th className="py-4 px-4">Pay Structure</th>
                                            <th className="py-4 px-4">Workload / Days Worked</th>
                                            <th className="py-4 px-4 text-right">Daily / Base Rate</th>
                                            <th className="py-4 px-4 text-right">Total Salary Earned</th>
                                            <th className="py-4 px-4 text-center">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 font-bold">
                                        {filteredEmployeeExpenses.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="py-12 text-center text-slate-300 font-bold italic text-sm">
                                                    No labour or employee salary records found for this period and project.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredEmployeeExpenses.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-purple-50/40 transition-colors group">
                                                    <td className="py-4 px-4 text-slate-900 font-black flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-black text-xs uppercase">
                                                            {row.employee_name ? row.employee_name[0] : 'W'}
                                                        </div>
                                                        <div>
                                                            <span>{row.employee_name}</span>
                                                            <p className="text-[9px] text-slate-400 font-bold uppercase">{row.type || 'Salary'}</p>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-slate-700 font-black text-[10px] uppercase">
                                                            {row.project_name}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-[10px] font-black uppercase">
                                                            {row.pay_type || 'Daily Wage'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-700 font-black">
                                                        {row.workload}
                                                    </td>
                                                    <td className="py-4 px-4 text-right text-slate-400">
                                                        {formatINR(row.applied_rate)}
                                                    </td>
                                                    <td className="py-4 px-4 text-right font-black text-purple-700 text-base">
                                                        {formatINR(row.amount)}
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                                                            <ShieldCheck size={12} /> Verified
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 5: VENDOR PAYMENTS */}
                    {activeTab === 'vendor' && (
                        <div className="bg-white rounded-[36px] p-8 border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                        <Truck size={20} className="text-indigo-600" />
                                        Vendor Invoices & Materials Remittance
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase mt-1">
                                        Showing {filteredVendorPayments.length} vendor disbursement records
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-slate-400 uppercase">Total Vendor Outflow</p>
                                    <p className="text-2xl font-black text-indigo-700">
                                        {formatINR(filteredVendorPayments.reduce((acc, v) => acc + (Number(v.amount) || 0), 0))}
                                    </p>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                                            <th className="py-4 px-4">Payment Date</th>
                                            <th className="py-4 px-4">Vendor Partner</th>
                                            <th className="py-4 px-4">Invoice #</th>
                                            <th className="py-4 px-4">Project</th>
                                            <th className="py-4 px-4">Payment Method</th>
                                            <th className="py-4 px-4">Notes</th>
                                            <th className="py-4 px-4 text-right">Amount Paid</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 font-bold">
                                        {filteredVendorPayments.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="py-12 text-center text-slate-300 font-bold italic text-sm">
                                                    No vendor payment records found
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredVendorPayments.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                                                    <td className="py-4 px-4 text-slate-500 font-semibold">{row.payment_date}</td>
                                                    <td className="py-4 px-4 text-slate-900 font-black">{row.vendor_name}</td>
                                                    <td className="py-4 px-4 text-indigo-600 font-black">#{row.invoice_no}</td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-700 font-black text-[10px] uppercase">
                                                            {row.project_name}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500 uppercase font-bold">{row.payment_method || 'Bank'}</td>
                                                    <td className="py-4 px-4 text-slate-400 max-w-[200px] truncate">{row.notes || '—'}</td>
                                                    <td className="py-4 px-4 text-right font-black text-indigo-700 text-sm">
                                                        {formatINR(row.amount)}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 6: PETTY CASH */}
                    {activeTab === 'petty' && (
                        <div className="bg-white rounded-[36px] p-8 border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                        <Receipt size={20} className="text-emerald-600" />
                                        Site Petty Cash Debits
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase mt-1">
                                        Approved Daily Site Out-of-Pocket Expenses ({filteredPettyCash.length} entries)
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-slate-400 uppercase">Total Petty Outflow</p>
                                    <p className="text-2xl font-black text-emerald-700">
                                        {formatINR(filteredPettyCash.reduce((acc, p) => acc + (Number(p.amount) || 0), 0))}
                                    </p>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                                            <th className="py-4 px-4">Date</th>
                                            <th className="py-4 px-4">Category</th>
                                            <th className="py-4 px-4">Remarks / Purpose</th>
                                            <th className="py-4 px-4">Staff / Spender</th>
                                            <th className="py-4 px-4">Project</th>
                                            <th className="py-4 px-4">Status</th>
                                            <th className="py-4 px-4 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 font-bold">
                                        {filteredPettyCash.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="py-12 text-center text-slate-300 font-bold italic text-sm">
                                                    No petty cash logs found
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredPettyCash.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                                                    <td className="py-4 px-4 text-slate-500 font-semibold">{row.date}</td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase">
                                                            {row.category || 'General'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-900 font-bold">{row.remarks}</td>
                                                    <td className="py-4 px-4 text-slate-600">{row.staff_name}</td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-700 font-black text-[10px] uppercase">
                                                            {row.project_name}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4">
                                                        <span className="text-[10px] font-black text-emerald-600 uppercase">
                                                            ● {row.status || 'Approved'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-right font-black text-emerald-700 text-sm">
                                                        {formatINR(row.amount)}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 7: MATERIAL SPENT */}
                    {activeTab === 'material' && (
                        <div className="bg-white rounded-[36px] p-8 border border-slate-100 shadow-sm space-y-6 animate-in fade-in duration-300">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                        <ShoppingCart size={20} className="text-amber-600" />
                                        Direct Site Material Procurements
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase mt-1">
                                        Hardware, Timber, Electricals & Consumables Procured On-Site ({filteredMaterialSpent.length} entries)
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-slate-400 uppercase">Total Material Spend</p>
                                    <p className="text-2xl font-black text-amber-700">
                                        {formatINR(filteredMaterialSpent.reduce((acc, m) => acc + (Number(m.amount) || 0), 0))}
                                    </p>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                                            <th className="py-4 px-4">Purchase Date</th>
                                            <th className="py-4 px-4">Biller / Supplier</th>
                                            <th className="py-4 px-4">Items Description</th>
                                            <th className="py-4 px-4">Project</th>
                                            <th className="py-4 px-4">Payment Method</th>
                                            <th className="py-4 px-4">Procured By</th>
                                            <th className="py-4 px-4 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 font-bold">
                                        {filteredMaterialSpent.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="py-12 text-center text-slate-300 font-bold italic text-sm">
                                                    No direct material purchase records found
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredMaterialSpent.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                                                    <td className="py-4 px-4 text-slate-500 font-semibold">{row.date}</td>
                                                    <td className="py-4 px-4 text-slate-900 font-black">{row.biller_name}</td>
                                                    <td className="py-4 px-4 text-slate-700 font-bold max-w-[220px] truncate">{row.items_summary}</td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-700 font-black text-[10px] uppercase">
                                                            {row.project_name}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500 uppercase text-[10px] font-bold">{row.payment_method || 'Direct'}</td>
                                                    <td className="py-4 px-4 text-slate-600">{row.paid_by || 'Staff'}</td>
                                                    <td className="py-4 px-4 text-right font-black text-amber-700 text-sm">
                                                        {formatINR(row.amount)}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
